/*
 * DEL 4 — the signature transformation visual.
 * AUTO-PLAY LOOP (no scroll dependency): cycles through 5 stages on a timer
 *   Source -> Map -> Model -> Engine -> Surface
 * Counters animate up each time their stage shows; the bottom stat strip always
 * shows the proof (1,240 dependencies, 62 inputs, 0 deviations, 100% parity).
 * Timeline nodes are clickable. Includes a Share / copy-link action.
 */
import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSpreadsheet, GitBranch, Boxes, Cpu, LayoutDashboard, Lock, KeyRound,
  ShieldCheck, ScrollText, Check, CheckCircle2, Share2, Linkedin,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";

const STAGE_MS = 3200;
const STAGE_ICONS = [FileSpreadsheet, GitBranch, Boxes, Cpu, LayoutDashboard];
const BADGE_ICONS = [Lock, KeyRound, ShieldCheck, ScrollText];
const TONE = ["red", "blue", "blue", "green", "green"];
const toneText = { red: "text-[var(--red)]", blue: "text-[var(--blue-bright)]", green: "text-[var(--green)]" };
const toneBorder = { red: "border-[rgba(244,63,94,0.4)]", blue: "border-[var(--line-strong)]", green: "border-[rgba(16,185,129,0.45)]" };
const toneVar = { red: "var(--red)", blue: "var(--blue-bright)", green: "var(--green)" };

const SOURCE_CELLS = [
  "=VLOOKUP(", "=SUM(B2:B40)", "#REF!", "=IF(D5>0",
  "B4*1.0825", "=PMT(rate)", "??", "=A2+C2",
  "=INDEX(M:M", "#REF!", "=CONCAT(", "0.0825",
  "=SUMIF(R", "=MAX(0,x)", "??", "=ROUND(Q",
];
const MODEL_INPUTS = [
  ["unitPrice", "number"], ["qty", "int"], ["discount", "number"], ["region", "enum"],
  ["taxRule", "enum"], ["currency", "enum"], ["validFrom", "date"], ["marginFloor", "number"],
];

/* Counts 0 -> target with an ease-out cubic whenever `play` flips true. */
const CountUp = ({ target, play, locale }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!play) { setN(0); return; }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [play, target]);
  return <>{Math.round(n).toLocaleString(locale)}</>;
};

const StageVisual = ({ index, active, locale, seq }) => {
  if (index === 0) {
    return (
      <div className="w-full max-w-md">
        <div className="grid grid-cols-4 gap-1.5" aria-hidden="true">
          {SOURCE_CELLS.map((c, i) => {
            const bad = c === "#REF!" || c === "??";
            return (
              <span key={i} className={`font-mono text-[9px] sm:text-[10px] leading-none px-1.5 py-2 rounded border truncate ${bad ? "border-[rgba(244,63,94,0.5)] text-[var(--red)] bg-[rgba(244,63,94,0.08)]" : "border-[var(--line)] text-[var(--muted)] bg-white/[0.02]"}`}>
                {c}
              </span>
            );
          })}
        </div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[rgba(244,63,94,0.35)] bg-[rgba(244,63,94,0.07)] px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--red)] animate-pulse-soft" />
          <span className="font-mono text-[11px] text-[var(--red)]">{seq.sourceTag}</span>
        </div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="w-full max-w-xl text-center">
        <svg viewBox="0 0 240 120" className="w-full max-h-[220px]" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="seq-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#3b82f6" />
              <stop offset="0.5" stopColor="#00e5ff" />
              <stop offset="1" stopColor="#34d399" />
            </linearGradient>
          </defs>
          {["M26,24 C96,24 96,60 150,60", "M26,60 C96,60 96,60 150,60", "M26,96 C96,60 96,60 150,60", "M26,42 C96,42 96,60 150,60", "M26,78 C96,78 96,60 150,60", "M150,60 C198,60 198,30 218,30", "M150,60 C198,60 198,90 218,90"].map((d, i) => (
            <path key={i} d={d} stroke="url(#seq-edge)" strokeWidth="1.3" className="trace-line" opacity="0.85" />
          ))}
          {[24, 42, 60, 78, 96].map((cy, i) => (
            <g key={i}>
              <circle cx="26" cy={cy} r="5" fill="#0b111e" stroke="#3b82f6" strokeWidth="1.4" />
              <circle cx="26" cy={cy} r="2" fill="#5b9cff" className="animate-pulse-soft" />
            </g>
          ))}
          <rect x="141" y="51" width="18" height="18" rx="5" fill="#0b111e" stroke="#00e5ff" strokeWidth="1.4" />
          <circle cx="150" cy="60" r="2.4" fill="#00e5ff" className="animate-pulse-soft" />
          {[30, 90].map((cy, i) => (
            <circle key={i} cx="218" cy={cy} r="5" fill="#0b111e" stroke="#34d399" strokeWidth="1.4" />
          ))}
        </svg>
        <div className="mt-4 font-display text-3xl sm:text-4xl font-bold text-gradient metric-glow" data-testid="sequence-stage1-count">
          <CountUp target={1240} play={active === 1} locale={locale} />
        </div>
        <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-[var(--muted-2)]">{seq.stats[0].label}</div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="w-full max-w-md">
        <div className="grid grid-cols-2 gap-1.5" aria-hidden="true">
          {MODEL_INPUTS.map(([k, ty]) => (
            <div key={k} className="flex items-center justify-between font-mono text-[10px] px-2.5 py-2 rounded-lg bg-white/[0.02] border border-[var(--line)]">
              <span className="flex items-center gap-1.5 text-[var(--blue-bright)]"><Check size={11} className="text-[var(--green)]" />{k}</span>
              <span className="text-[var(--muted-2)]">{ty}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 text-center">
          <span className="font-display text-3xl sm:text-4xl font-bold text-gradient metric-glow" data-testid="sequence-stage2-count">
            <CountUp target={62} play={active === 2} locale={locale} />
          </span>
          <span className="ml-2 font-mono text-[11px] uppercase tracking-widest text-[var(--muted-2)]">{seq.stats[1].label}</span>
        </div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="w-full max-w-md flex flex-col items-center">
        <div className="relative grid place-items-center h-24 w-24">
          <span className="absolute inset-0 rounded-3xl border border-[rgba(16,185,129,0.45)] bg-[rgba(16,185,129,0.06)] validate-pulse" />
          <span className="absolute inset-2 rounded-2xl border border-dashed border-[rgba(59,130,246,0.45)] animate-spin-slow" />
          <Cpu className="relative text-[var(--green)]" size={34} />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-2 w-full max-w-xs">
          {seq.badges.map((b, i) => {
            const Icon = BADGE_ICONS[i] || ShieldCheck;
            return (
              <div key={b} className="flex items-center gap-2 rounded-lg border border-[rgba(16,185,129,0.35)] bg-[rgba(16,185,129,0.05)] px-3 py-2">
                <Icon size={14} className="text-[var(--green)]" />
                <span className="font-mono text-[11px] text-white/90">{b}</span>
                <CheckCircle2 size={13} className="ml-auto text-[var(--green)]" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="w-full max-w-lg">
      <div className="panel rounded-2xl p-5 border-[rgba(16,185,129,0.3)] overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[var(--green)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] validate-pulse" /> {seq.dashTitle}
          </span>
          <LayoutDashboard size={15} className="text-[var(--muted-2)]" />
        </div>
        <div className="flex items-end gap-1.5 h-16 mb-4">
          {[44, 68, 52, 82, 60, 90, 72].map((h, i) => (
            <span key={i} className="flex-1 rounded-sm bg-gradient-to-t from-[var(--blue)] to-[var(--green)]" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg bg-white/[0.02] border border-[var(--line)] p-2.5">
              <div className="h-1.5 w-10 rounded-full bg-[var(--line-strong)]" />
              <div className="mt-2 h-1 w-full rounded-full bg-[var(--line)]" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-center gap-2.5 rounded-2xl border border-[rgba(16,185,129,0.4)] bg-[rgba(16,185,129,0.07)] px-4 py-3" data-testid="sequence-parity">
        <CheckCircle2 size={20} className="text-[var(--green)]" />
        <span className="font-display text-lg sm:text-xl font-bold text-white">
          <CountUp target={100} play={active === 4} locale={locale} />% parity
        </span>
        <span className="font-mono text-[11px] text-[var(--muted-2)]">· 0 {seq.stats[2].label}</span>
      </div>
    </div>
  );
};

export const TransformationSequence = () => {
  const { t, lang } = useLang();
  const seq = t.sequence;
  const locale = lang === "da" ? "da-DK" : "en-US";
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % 5), STAGE_MS);
    return () => clearInterval(id);
  }, [inView]);

  const ActiveIcon = STAGE_ICONS[active];

  const share = async () => {
    const url = `${window.location.origin}/#transformation`;
    const data = { title: "ReverseAI.dk", text: seq.title, url };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch (e) { /* fall through */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(seq.shareCopied);
    } catch (e) {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener");
    }
  };

  const shareLinkedIn = () => {
    const url = `${window.location.origin}/#transformation`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener");
  };

  return (
    <section ref={ref} id="transformation" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden" data-testid="sequence-section">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" style={{ zIndex: 0 }} />
      <div
        className="glow-orb animate-glow pointer-events-none"
        style={{ zIndex: 0, width: 600, height: 460, top: "10%", left: "50%", transform: "translateX(-50%)", background: toneVar[TONE[active]], opacity: 0.14, transition: "background 1s ease" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-5xl px-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--blue-bright)]">{seq.eyebrow}</span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={share} className="btn-ghost !py-2 !px-3.5 text-xs" data-testid="sequence-share-btn">
              <Share2 size={14} /> {seq.share}
            </button>
            <button type="button" onClick={shareLinkedIn} aria-label={seq.shareLinkedIn} className="grid place-items-center h-9 w-9 rounded-lg border border-[var(--line-strong)] text-white hover:border-[var(--blue)] hover:text-[var(--blue-bright)] transition-colors" data-testid="sequence-linkedin-btn">
              <Linkedin size={15} />
            </button>
          </div>
        </div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-tight max-w-3xl" data-testid="sequence-title">
          {seq.title}
        </h2>
        <p className="mt-3 text-sm md:text-base text-muted max-w-2xl">{seq.sub}</p>

        {/* Auto-playing stage viewport (fixed height so nothing jumps) */}
        <div className="mt-10 glass-card depth-card rounded-3xl p-6 lg:p-8">
          <div className="relative h-[300px] sm:h-[340px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 22, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.98 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute inset-0 flex items-center justify-center px-2"
                data-testid="sequence-viewport"
              >
                <StageVisual index={active} active={active} locale={locale} seq={seq} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Caption */}
          <div className="flex items-center gap-3 mt-2 min-h-[52px]">
            <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-xl border bg-white/[0.03] ${toneBorder[TONE[active]]} ${toneText[TONE[active]]}`}>
              <ActiveIcon size={19} />
            </span>
            <div>
              <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${toneText[TONE[active]]}`}>
                {String(active + 1).padStart(2, "0")} · {seq.steps[active].label}
              </div>
              <p className="text-sm sm:text-base text-white/90 leading-snug" data-testid="sequence-caption">{seq.steps[active].caption}</p>
            </div>
          </div>

          {/* Clickable timeline */}
          <div className="relative flex items-center justify-between mt-5 mb-1" data-testid="sequence-timeline">
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-[var(--line-strong)]" />
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-[var(--blue)] via-[var(--cyan)] to-[var(--green)] transition-[width] duration-500"
              style={{ width: `${((active + 1) / 5) * 100}%` }}
            />
            {seq.steps.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={s.label}
                className="relative grid place-items-center h-6 w-6 rounded-full bg-[#0b111e] border transition-colors duration-300"
                style={{ borderColor: i <= active ? toneVar[TONE[i]] : "var(--line-strong)" }}
                data-testid={`sequence-node-${i}`}
              >
                <span className="h-2 w-2 rounded-full transition-colors duration-300" style={{ background: i <= active ? toneVar[TONE[i]] : "var(--line-strong)" }} />
              </button>
            ))}
          </div>
        </div>

        {/* Persistent stat strip — the proof always stays visible */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8" data-testid="sequence-stats">
          {[
            { v: (1240).toLocaleString(locale), l: seq.stats[0].label },
            { v: "62", l: seq.stats[1].label },
            { v: "0", l: seq.stats[2].label },
            { v: "100%", l: seq.stats[3].label },
          ].map((s, i) => (
            <div key={i} className="glass rounded-2xl p-4">
              <div className="font-display text-xl lg:text-2xl font-bold text-gradient">{s.v}</div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
